import type {
  LoginRequest,
  RegisterRequest,
  VerifyEmailRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  AuthResponse,
  MessageResponse,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  OrganizationResponse,
  OrganizationDetailResponse,
  AddMemberRequest,
  UpdateMemberRoleRequest,
  MemberResponse,
  CreateTeamRequest,
  UpdateTeamRequest,
  TeamResponse,
  AddTeamMemberRequest,
  TeamMemberResponse,
  ErrorResponse,
  BillingStatusResponse,
  CreateCheckoutRequest,
  CheckoutResponse,
  CreatePortalRequest,
  PortalResponse,
  InvoicesResponse,
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectResponse,
  ThoughtSessionResponse,
  OutlineResponse,
  OutlineNodeResponse,
  GenerateOutlineRequest,
} from "../types/api";
import type {
  VoiceProfile,
  VoiceProfileSummary,
  VoiceProfileAnalytics,
  VoicePreferences,
} from "../types/voiceProfile";

// API Error class
export class APIError extends Error {
  statusCode: number;
  data?: unknown;

  constructor(message: string, statusCode: number, data?: unknown) {
    super(message);
    this.name = "APIError";
    this.statusCode = statusCode;
    this.data = data;
  }
}

// Base URL configuration
const getBaseURL = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // Default to /api which will be proxied to backend in development
  return "/api";
};

// Generic fetch wrapper with error handling
async function fetchAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const baseURL = getBaseURL();
  const url = `${baseURL}${endpoint}`;

  // Always include credentials for cookie-based sessions
  const config: RequestInit = {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    // Parse response body
    let data: unknown;
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Handle error responses
    if (!response.ok) {
      const errorMessage =
        typeof data === "object" && data !== null && "error" in data
          ? (data as ErrorResponse).error
          : typeof data === "string"
            ? data
            : `Request failed with status ${response.status}`;

      throw new APIError(errorMessage, response.status, data);
    }

    return data as T;
  } catch (error) {
    // Re-throw APIError as-is
    if (error instanceof APIError) {
      throw error;
    }

    // Handle network errors
    if (error instanceof TypeError && error.message === "Failed to fetch") {
      throw new APIError("Network error: Unable to connect to server", 0, error);
    }

    // Handle other errors
    throw new APIError(error instanceof Error ? error.message : "Unknown error occurred", 0, error);
  }
}

// Auth API functions
export const authAPI = {
  login: (data: LoginRequest): Promise<AuthResponse> =>
    fetchAPI<AuthResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  register: (data: RegisterRequest): Promise<AuthResponse> =>
    fetchAPI<AuthResponse>("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  logout: (): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>("/auth/logout", {
      method: "POST",
    }),

  me: (): Promise<AuthResponse> => fetchAPI<AuthResponse>("/auth/me"),

  verifyEmail: (data: VerifyEmailRequest): Promise<AuthResponse> =>
    fetchAPI<AuthResponse>("/auth/verify-email", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  resendVerification: (): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>("/auth/resend-verification", {
      method: "POST",
    }),

  forgotPassword: (data: ForgotPasswordRequest): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  resetPassword: (data: ResetPasswordRequest): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  changePassword: (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateEmail: (data: { email: string; password: string }): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>("/auth/email", {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteAccount: (data: { password: string }): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>("/auth/account", {
      method: "DELETE",
      body: JSON.stringify(data),
    }),
};

// Organization API functions
export const organizationAPI = {
  getAll: async (): Promise<OrganizationResponse[]> => {
    const response = await fetchAPI<{ organizations: OrganizationResponse[] }>("/organizations");
    return response.organizations;
  },

  getById: async (id: string): Promise<OrganizationDetailResponse> => {
    const response = await fetchAPI<OrganizationDetailResponse>(`/organizations/${id}`);
    return response;
  },

  create: async (data: CreateOrganizationRequest): Promise<OrganizationResponse> => {
    const response = await fetchAPI<{ organization: OrganizationResponse }>("/organizations", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.organization;
  },

  update: async (id: string, data: UpdateOrganizationRequest): Promise<OrganizationResponse> => {
    const response = await fetchAPI<{ organization: OrganizationResponse }>(
      `/organizations/${id}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
    return response.organization;
  },

  delete: (id: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(`/organizations/${id}`, {
      method: "DELETE",
    }),

  getMembers: async (id: string): Promise<MemberResponse[]> => {
    const response = await fetchAPI<{ members: MemberResponse[] }>(`/organizations/${id}/members`);
    return response.members;
  },

  addMember: async (id: string, data: AddMemberRequest): Promise<MemberResponse> => {
    const response = await fetchAPI<{ member: MemberResponse }>(`/organizations/${id}/members`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.member;
  },

  updateMemberRole: async (
    id: string,
    userId: string,
    data: UpdateMemberRoleRequest,
  ): Promise<MemberResponse> => {
    const response = await fetchAPI<{ member: MemberResponse }>(
      `/organizations/${id}/members/${userId}/role`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
    return response.member;
  },

  removeMember: (id: string, userId: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(`/organizations/${id}/members/${userId}`, {
      method: "DELETE",
    }),
};

// Team API functions
export const teamAPI = {
  getAll: async (orgId: string): Promise<TeamResponse[]> => {
    const response = await fetchAPI<{ teams: TeamResponse[] }>(`/organizations/${orgId}/teams`);
    return response.teams;
  },

  getById: async (orgId: string, teamId: string): Promise<TeamResponse> => {
    const response = await fetchAPI<{ team: TeamResponse }>(
      `/organizations/${orgId}/teams/${teamId}`,
    );
    return response.team;
  },

  create: async (orgId: string, data: CreateTeamRequest): Promise<TeamResponse> => {
    const response = await fetchAPI<{ team: TeamResponse }>(`/organizations/${orgId}/teams`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.team;
  },

  update: async (orgId: string, teamId: string, data: UpdateTeamRequest): Promise<TeamResponse> => {
    const response = await fetchAPI<{ team: TeamResponse }>(
      `/organizations/${orgId}/teams/${teamId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
    return response.team;
  },

  delete: (orgId: string, teamId: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(`/organizations/${orgId}/teams/${teamId}`, {
      method: "DELETE",
    }),

  getMembers: async (orgId: string, teamId: string): Promise<TeamMemberResponse[]> => {
    const response = await fetchAPI<{ members: TeamMemberResponse[] }>(
      `/organizations/${orgId}/teams/${teamId}/members`,
    );
    return response.members;
  },

  addMember: async (
    orgId: string,
    teamId: string,
    data: AddTeamMemberRequest,
  ): Promise<TeamMemberResponse> => {
    const response = await fetchAPI<{ member: TeamMemberResponse }>(
      `/organizations/${orgId}/teams/${teamId}/members`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return response.member;
  },

  removeMember: (orgId: string, teamId: string, userId: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(`/organizations/${orgId}/teams/${teamId}/members/${userId}`, {
      method: "DELETE",
    }),
};

// Billing API functions
export const billingAPI = {
  getStatus: (orgId: string): Promise<BillingStatusResponse> =>
    fetchAPI<BillingStatusResponse>(`/organizations/${orgId}/billing`),

  createCheckout: (orgId: string, data: CreateCheckoutRequest): Promise<CheckoutResponse> =>
    fetchAPI<CheckoutResponse>(`/organizations/${orgId}/billing/checkout`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  createPortal: (orgId: string, data: CreatePortalRequest): Promise<PortalResponse> =>
    fetchAPI<PortalResponse>(`/organizations/${orgId}/billing/portal`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  cancel: (orgId: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(`/organizations/${orgId}/billing/cancel`, {
      method: "POST",
    }),

  getInvoices: (orgId: string): Promise<InvoicesResponse> =>
    fetchAPI<InvoicesResponse>(`/organizations/${orgId}/billing/invoices`),
};

// Project API functions
export const projectAPI = {
  getAll: async (orgId: string): Promise<ProjectResponse[]> => {
    const response = await fetchAPI<{ projects: ProjectResponse[] }>(
      `/organizations/${orgId}/projects`,
    );
    return response.projects;
  },

  getById: async (orgId: string, projectId: string): Promise<ProjectResponse> => {
    const response = await fetchAPI<{ project: ProjectResponse }>(
      `/organizations/${orgId}/projects/${projectId}`,
    );
    return response.project;
  },

  create: async (orgId: string, data: CreateProjectRequest): Promise<ProjectResponse> => {
    const response = await fetchAPI<{ project: ProjectResponse }>(
      `/organizations/${orgId}/projects`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return response.project;
  },

  update: async (
    orgId: string,
    projectId: string,
    data: UpdateProjectRequest,
  ): Promise<ProjectResponse> => {
    const response = await fetchAPI<{ project: ProjectResponse }>(
      `/organizations/${orgId}/projects/${projectId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
    return response.project;
  },

  delete: (orgId: string, projectId: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(`/organizations/${orgId}/projects/${projectId}`, {
      method: "DELETE",
    }),
};

// Thought Session API functions
export interface StreamMessageOptions {
  onChunk?: (text: string) => void;
  onDone?: (session: ThoughtSessionResponse) => void;
  onError?: (error: Error) => void;
}

export const thoughtSessionAPI = {
  getOrCreate: async (orgId: string, projectId: string): Promise<ThoughtSessionResponse> => {
    // First try to get existing active sessions
    const response = await fetchAPI<{
      sessions: ThoughtSessionResponse[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/organizations/${orgId}/projects/${projectId}/sessions?status=active&limit=1`);

    const existingSession = response.sessions[0];
    if (existingSession) {
      return existingSession;
    }

    // Create a new session if none exists
    const createResponse = await fetchAPI<{
      session: ThoughtSessionResponse;
    }>(`/organizations/${orgId}/projects/${projectId}/sessions`, {
      method: "POST",
      body: JSON.stringify({}),
    });

    return createResponse.session;
  },

  getById: async (orgId: string, sessionId: string): Promise<ThoughtSessionResponse | null> => {
    try {
      const response = await fetchAPI<{ session: ThoughtSessionResponse }>(
        `/organizations/${orgId}/sessions/${sessionId}`,
      );
      return response.session;
    } catch (err) {
      if (err instanceof APIError && err.statusCode === 404) {
        return null;
      }
      throw err;
    }
  },

  addMessage: async (
    orgId: string,
    sessionId: string,
    content: string,
  ): Promise<ThoughtSessionResponse> => {
    const response = await fetchAPI<{
      session: ThoughtSessionResponse;
    }>(`/organizations/${orgId}/sessions/${sessionId}/messages`, {
      method: "POST",
      body: JSON.stringify({ content, generateAIResponse: false }),
    });

    return response.session;
  },

  editMessage: async (
    orgId: string,
    sessionId: string,
    messageIndex: number,
    content: string,
  ): Promise<ThoughtSessionResponse> => {
    const response = await fetchAPI<{
      session: ThoughtSessionResponse;
    }>(`/organizations/${orgId}/sessions/${sessionId}/messages/${messageIndex}`, {
      method: "PUT",
      body: JSON.stringify({ content }),
    });

    return response.session;
  },

  deleteMessage: async (
    orgId: string,
    sessionId: string,
    messageIndex: number,
  ): Promise<ThoughtSessionResponse> => {
    const response = await fetchAPI<{
      session: ThoughtSessionResponse;
    }>(`/organizations/${orgId}/sessions/${sessionId}/messages/${messageIndex}`, {
      method: "DELETE",
    });

    return response.session;
  },

  addMessageWithStream: async (
    orgId: string,
    sessionId: string,
    content: string,
    options: StreamMessageOptions = {},
  ): Promise<ThoughtSessionResponse> => {
    const baseURL = getBaseURL();
    const url = `${baseURL}/organizations/${orgId}/sessions/${sessionId}/messages`;

    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ content, generateAIResponse: true, stream: true }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.error || `Request failed with status ${response.status}`,
        response.status,
        errorData,
      );
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new APIError("No response body", 0);
    }

    const decoder = new TextDecoder();
    let finalSession: ThoughtSessionResponse | null = null;
    let buffer = "";

    /** Process SSE lines from a raw event block. */
    const processSSEBlock = (block: string) => {
      for (const line of block.split("\n")) {
        if (line.startsWith("data: ")) {
          const data = line.slice(6);
          try {
            const parsed = JSON.parse(data);

            if (parsed.error) {
              const error = parsed.code
                ? new APIError(parsed.error, 0, { code: parsed.code })
                : new Error(parsed.error);
              options.onError?.(error);
              throw error;
            }

            if (parsed.text) {
              options.onChunk?.(parsed.text);
            }

            if (parsed.done && parsed.session) {
              finalSession = parsed.session;
              options.onDone?.(parsed.session);
            }
          } catch (parseError) {
            // Skip non-JSON lines
            if (data.trim() && !(parseError instanceof SyntaxError)) {
              throw parseError;
            }
          }
        }
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          // Drain any remaining buffered event that wasn't terminated by a
          // double-newline delimiter (e.g. server closed the stream immediately
          // after the last data line).
          if (buffer.trim()) {
            processSSEBlock(buffer);
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        // Normalize CRLF → LF so the parser works with any SSE delimiter style
        buffer = buffer.replace(/\r\n/g, "\n");
        // SSE events are separated by double newlines; split on them
        const events = buffer.split("\n\n");
        // The last element may be an incomplete event — keep it in the buffer
        buffer = events.pop() || "";

        for (const event of events) {
          processSSEBlock(event);
        }
      }
    } finally {
      reader.releaseLock();
    }

    if (!finalSession) {
      throw new APIError("No session returned from stream", 0);
    }

    return finalSession;
  },

  updateStatus: async (
    orgId: string,
    sessionId: string,
    status: ThoughtSessionResponse["status"],
  ): Promise<ThoughtSessionResponse> => {
    const response = await fetchAPI<{ session: ThoughtSessionResponse }>(
      `/organizations/${orgId}/sessions/${sessionId}/status`,
      {
        method: "PUT",
        body: JSON.stringify({ status }),
      },
    );

    return response.session;
  },

  search: async (
    orgId: string,
    query: string,
    options: { limit?: number; offset?: number } = {},
  ): Promise<{
    sessions: ThoughtSessionResponse[];
    pagination: { total: number; limit: number; offset: number };
  }> => {
    const params = new URLSearchParams({ q: query });
    if (options.limit) params.set("limit", String(options.limit));
    if (options.offset) params.set("offset", String(options.offset));

    return fetchAPI<{
      sessions: ThoughtSessionResponse[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/organizations/${orgId}/sessions/search?${params.toString()}`);
  },
};

// Voice Profile API functions
export const voiceProfileAPI = {
  getProfile: async (): Promise<VoiceProfile> => {
    const response = await fetchAPI<{
      profile: {
        id: string;
        user_id: string;
        preferences: VoicePreferences;
        suggestion_stats: VoiceProfile["suggestionStats"];
        created_at: string;
        updated_at: string;
      };
    }>("/user/voice-profile");
    return {
      id: response.profile.id,
      userId: response.profile.user_id,
      preferences: response.profile.preferences,
      suggestionStats: response.profile.suggestion_stats,
      createdAt: response.profile.created_at,
      updatedAt: response.profile.updated_at,
    };
  },

  getSummary: async (): Promise<VoiceProfileSummary> => {
    const response = await fetchAPI<{
      summary: {
        user_id: string;
        tone: string | null;
        sentence_complexity: string | null;
        vocabulary_level: string | null;
        literary_devices: string[];
        acceptance_rates: { [category: string]: number };
        total_suggestions_processed: number;
        profile_age: string;
      };
    }>("/user/voice-profile/summary");
    return {
      userId: response.summary.user_id,
      tone: response.summary.tone,
      sentenceComplexity: response.summary.sentence_complexity,
      vocabularyLevel: response.summary.vocabulary_level,
      literaryDevices: response.summary.literary_devices,
      acceptanceRates: response.summary.acceptance_rates,
      totalSuggestionsProcessed: response.summary.total_suggestions_processed,
      profileAge: response.summary.profile_age,
    };
  },

  getAnalytics: async (): Promise<VoiceProfileAnalytics> => {
    const response = await fetchAPI<{
      analytics: {
        acceptance_rates_by_category: { [category: string]: number };
        overall_acceptance_rate: number;
        total_suggestions: number;
        most_accepted_category: string | null;
        least_accepted_category: string | null;
      };
    }>("/user/voice-profile/analytics");
    return {
      acceptanceRatesByCategory: response.analytics.acceptance_rates_by_category,
      overallAcceptanceRate: response.analytics.overall_acceptance_rate,
      totalSuggestions: response.analytics.total_suggestions,
      mostAcceptedCategory: response.analytics.most_accepted_category,
      leastAcceptedCategory: response.analytics.least_accepted_category,
    };
  },

  updatePreferences: async (preferences: Partial<VoicePreferences>): Promise<VoiceProfile> => {
    const response = await fetchAPI<{
      profile: {
        id: string;
        user_id: string;
        preferences: VoicePreferences;
        updated_at: string;
      };
    }>("/user/voice-profile/preferences", {
      method: "PATCH",
      body: JSON.stringify(preferences),
    });
    return {
      id: response.profile.id,
      userId: response.profile.user_id,
      preferences: response.profile.preferences,
      suggestionStats: {},
      createdAt: "",
      updatedAt: response.profile.updated_at,
    };
  },

  reset: async (): Promise<VoiceProfile> => {
    const response = await fetchAPI<{
      profile: {
        id: string;
        user_id: string;
        preferences: VoicePreferences;
        suggestion_stats: VoiceProfile["suggestionStats"];
        updated_at: string;
      };
    }>("/user/voice-profile/reset", {
      method: "POST",
    });
    return {
      id: response.profile.id,
      userId: response.profile.user_id,
      preferences: response.profile.preferences,
      suggestionStats: response.profile.suggestion_stats,
      createdAt: "",
      updatedAt: response.profile.updated_at,
    };
  },

  delete: (): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>("/user/voice-profile", {
      method: "DELETE",
    }),
};

// Outline API functions
export const outlineAPI = {
  get: async (orgId: string, projectId: string): Promise<OutlineResponse | null> => {
    const response = await fetchAPI<{ outline: OutlineResponse | null }>(
      `/organizations/${orgId}/projects/${projectId}/outline`,
    );
    return response.outline;
  },

  update: async (
    orgId: string,
    projectId: string,
    nodes: OutlineNodeResponse[],
  ): Promise<OutlineResponse> => {
    const response = await fetchAPI<{ outline: OutlineResponse }>(
      `/organizations/${orgId}/projects/${projectId}/outline`,
      {
        method: "PUT",
        body: JSON.stringify({ nodes }),
      },
    );
    return response.outline;
  },

  generate: async (
    orgId: string,
    projectId: string,
    options: GenerateOutlineRequest = {},
  ): Promise<OutlineResponse> => {
    const response = await fetchAPI<{ outline: OutlineResponse }>(
      `/organizations/${orgId}/projects/${projectId}/outline/generate`,
      {
        method: "POST",
        body: JSON.stringify(options),
      },
    );
    return response.outline;
  },

  expandPoint: async (
    orgId: string,
    projectId: string,
    nodeId: string,
  ): Promise<OutlineResponse> => {
    const response = await fetchAPI<{ outline: OutlineResponse }>(
      `/organizations/${orgId}/projects/${projectId}/outline/expand`,
      {
        method: "POST",
        body: JSON.stringify({ nodeId }),
      },
    );
    return response.outline;
  },

  deleteNode: async (
    orgId: string,
    projectId: string,
    nodeId: string,
  ): Promise<OutlineResponse> => {
    const response = await fetchAPI<{ outline: OutlineResponse }>(
      `/organizations/${orgId}/projects/${projectId}/outline/nodes/${nodeId}`,
      {
        method: "DELETE",
      },
    );
    return response.outline;
  },
};

// AI status API
export type AIProvider = "anthropic" | "openai" | "custom";

export interface AIStatusResponse {
  configured: boolean;
  hasConfig: boolean;
  needsVerification?: boolean;
  byokAllowed?: boolean;
  provider: AIProvider | null;
  rate_limit: {
    requests_remaining: number;
    tokens_remaining: number;
    reset_at: string;
  };
}

export interface AIConfigureResponse {
  configured: boolean;
  provider: AIProvider | null;
  message: string;
}

export const aiAPI = {
  getStatus: (): Promise<AIStatusResponse> => fetchAPI<AIStatusResponse>("/user/ai/status"),

  configure: (
    apiKey: string,
    provider: AIProvider = "anthropic",
    baseUrl?: string,
    modelName?: string,
  ): Promise<AIConfigureResponse> =>
    fetchAPI<AIConfigureResponse>("/user/ai/configure", {
      method: "PUT",
      body: JSON.stringify({ apiKey, provider, baseUrl, modelName }),
    }),

  removeConfigure: (): Promise<AIConfigureResponse> =>
    fetchAPI<AIConfigureResponse>("/user/ai/configure", {
      method: "DELETE",
    }),

  verify: (): Promise<{ valid: boolean; error?: string }> =>
    fetchAPI<{ valid: boolean; error?: string }>("/user/ai/verify", {
      method: "POST",
    }),

  fetchModels: (
    baseUrl: string,
    apiKey?: string,
  ): Promise<{ ok: boolean; models: string[]; error?: string }> =>
    fetchAPI<{ ok: boolean; models: string[]; error?: string }>("/user/ai/models", {
      method: "POST",
      body: JSON.stringify({ baseUrl, apiKey }),
    }),
};

// Prerelease / Waitlist API
export const prereleaseAPI = {
  getConfig: (): Promise<{ prereleaseMode: boolean }> =>
    fetchAPI<{ prereleaseMode: boolean }>("/config/prerelease"),

  joinWaitlist: (email: string): Promise<{ message: string }> =>
    fetchAPI<{ message: string }>("/waitlist", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
};
// Deploy: bake in VITE_API_URL
